import type { CSSProperties } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CBadge } from "@coreui/react-pro";
import { DirectionIcon } from "@scryerlabs/ui";
import {
  usePreloadTradingStrategies,
  type BillingCycle,
  type TradingStrategyDto,
} from "../../../api/hooks/useTradingStrategies";
import {
  deriveSubscriptionDisplayStatus,
  isActiveSubscription,
  subscriptionStatusColor,
  useMySubscriptions,
} from "../../../api/hooks/useStrategySubscriptions";
import {
  isIncludedInMembership,
  useMyMemberships,
} from "../../../api/hooks/useMemberships";
import { myStrategyDetailPath } from "../../../routes/route-paths";

interface StrategySideTabsProps {
  /** Every side of the strategy, in side order. Fewer than two renders nothing. */
  sides: TradingStrategyDto[];
  currentId: string;
  /** The cycle a public strategy page is showing, carried to the side a tab opens. */
  cycle: BillingCycle;
  /** On the public strategy pages, a side the customer does not hold opens there too. */
  browsing: boolean;
}

function tabTarget(side: TradingStrategyDto, held: boolean, browsing: boolean, cycle: BillingCycle): string {
  if (held) {
    return `/subscriptions/${side.id}`;
  }

  if (browsing) {
    return `/strategies/${side.id}` + (cycle === "Yearly" ? "?cycle=Yearly" : "");
  }

  return myStrategyDetailPath(side.id);
}

// The selected tab takes the card's background and drops 1px over the card's top border, so the
// two read as one piece.
function tabStyle(selected: boolean): CSSProperties {
  const tabBackground = selected ? "var(--cui-card-bg, var(--cui-body-bg))" : "var(--cui-tertiary-bg)";

  return {
    position: "relative",
    zIndex: selected ? 2 : 1,
    marginBottom: -1,
    minWidth: 0,
    padding: "0.45rem 0.85rem",
    border: "1px solid var(--cui-border-color-translucent)",
    borderBottomColor: selected ? tabBackground : "var(--cui-border-color-translucent)",
    borderTopLeftRadius: "var(--cui-border-radius-lg)",
    borderTopRightRadius: "var(--cui-border-radius-lg)",
    background: tabBackground,
    color: selected ? "var(--cui-body-color)" : "var(--cui-secondary-color)",
    textDecoration: "none",
  };
}

/**
 * A strategy's sides as tabs on top of its page card. Each tab is that side's own page, so the
 * figures under it are always the side's own.
 */
export function StrategySideTabs({ sides, currentId, cycle, browsing }: StrategySideTabsProps) {
  const { t } = useTranslation("tradingStrategies");
  const location = useLocation();
  const subscriptions = useMySubscriptions().data ?? [];
  const memberships = useMyMemberships().data ?? [];
  // Each tab is another page, so the other sides are loaded now and a tab opens without a spinner.
  const otherSides = sides.filter((side) => side.id !== currentId);
  usePreloadTradingStrategies(otherSides);

  if (sides.length < 2) {
    return null;
  }

  const currentPath = location.pathname + location.search;

  return (
    <div role="tablist" aria-label="Sides" className="strategy-side-tabs d-flex align-items-end gap-1">
      {sides.map((side) => {
        const selected = side.id === currentId;
        const heldSubscription = subscriptions.find(
          (subscription) => subscription.strategy.id === side.id && isActiveSubscription(subscription),
        );
        const held = heldSubscription !== undefined;
        // The side's own status, so a cancelled side never reads as simply subscribed.
        const heldStatus = heldSubscription ? deriveSubscriptionDisplayStatus(heldSubscription) : null;
        const included = !held && isIncludedInMembership(side.id, memberships);
        const target = selected ? currentPath : tabTarget(side, held, browsing, cycle);
        const direction = side.direction ?? 0;
        const sideWords = t(`sides.${direction}`, { defaultValue: side.publicName });
        const shortSideWords = t(`sidesShort.${direction}`, { defaultValue: side.publicName });

        return (
          <Link
            key={side.id}
            to={target}
            role="tab"
            aria-selected={selected}
            className="d-flex align-items-center gap-2"
            style={tabStyle(selected)}
          >
            {direction > 0 && (
              <DirectionIcon direction={direction} label={t(`traits.direction.${direction}`)} size={18} />
            )}
            <span className="fw-semibold text-nowrap">
              <span className="side-words-full">{sideWords}</span>
              <span className="side-words-short">{shortSideWords}</span>
            </span>
            {heldStatus && (
              <CBadge color={subscriptionStatusColor(heldStatus)} shape="rounded-pill">
                {heldStatus}
              </CBadge>
            )}
            {included && (
              <CBadge color="success" shape="rounded-pill">
                Included
              </CBadge>
            )}
          </Link>
        );
      })}
    </div>
  );
}
